import { useInView } from '../../../hooks/useInView';

interface MenuItem {
  name: string;
  description: string;
  duration?: string;
  price: string;
}

interface MenuCategory {
  id: string;
  label: string;
  enLabel: string;
  icon: string;
  image: string;
  items: MenuItem[];
  note?: string;
}

const menuCategories: MenuCategory[] = [
  {
    id: 'facial',
    label: 'フェイシャルエステ',
    enLabel: 'FACIAL',
    icon: 'ri-leaf-line',
    image: 'https://readdy.ai/api/search-image?query=luxury%20facial%20treatment%20skincare%20products%20on%20white%20marble%20surface%2C%20elegant%20glass%20bottles%20with%20serum%20and%20cream%2C%20soft%20natural%20lighting%2C%20premium%20beauty%20essentials%20arranged%20minimally%2C%20clean%20off-white%20background%20with%20subtle%20gold%20accents&width=600&height=400&seq=menu001&orientation=landscape',
    items: [
      { name: '毛穴洗浄クレンジングコース', description: 'クレンジング・毛穴洗浄・保湿パック', duration: '60分', price: '¥12,000' },
      { name: '保湿集中ケアコース', description: '高濃度ヒアルロン酸導入・鎮静マスク', duration: '90分', price: '¥16,000' },
      { name: 'フルフェイシャルコース', description: '全工程ケア・リフトアップマッサージ', duration: '120分', price: '¥22,000' },
    ],
    note: '初回カウンセリング（15分）無料',
  },
  {
    id: 'hair',
    label: '脱毛（パーツ別）',
    enLabel: 'HAIR REMOVAL',
    icon: 'ri-sparkling-line',
    image: 'https://readdy.ai/api/search-image?query=professional%20laser%20hair%20removal%20device%20on%20clean%20white%20surface%2C%20medical%20grade%20equipment%20with%20sleek%20modern%20design%2C%20clinical%20precision%20instrument%2C%20soft%20studio%20lighting%2C%20minimalist%20background%20in%20cream%20and%20white%20tones&width=600&height=400&seq=menu002&orientation=landscape',
    items: [
      { name: '顔全体', description: 'おでこ・頬・あご下・うなじ含む', price: '¥8,000' },
      { name: '両腕（手の甲含む）', description: '手の甲・前腕・上腕', price: '¥12,000' },
      { name: '両脚（足首まで）', description: 'ひざ下・ひざ上', price: '¥15,000' },
      { name: 'VIOセット', description: 'V・I・Oの3パーツセット', price: '¥18,000' },
      { name: '全身セット（VIO含む）', description: '顔・全身・VIO一括施術', price: '¥45,000' },
    ],
    note: '各施術は6回コース料金あり（10%割引）',
  },
  {
    id: 'eyelash',
    label: 'まつげケア',
    enLabel: 'EYELASH',
    icon: 'ri-eye-line',
    image: 'https://readdy.ai/api/search-image?query=close%20up%20of%20beautiful%20eyelash%20extension%20tools%20and%20accessories%20on%20white%20background%2C%20professional%20tweezers%20and%20adhesive%20arranged%20elegantly%2C%20beauty%20treatment%20supplies%20with%20soft%20bokeh%20background%2C%20high-end%20salon%20aesthetic&width=600&height=400&seq=menu003&orientation=landscape',
    items: [
      { name: 'シングルエクステ 80本', description: 'ナチュラルデザイン・1〜2週間もち', price: '¥6,000' },
      { name: 'シングルエクステ 120本', description: 'セミロングデザイン・2〜3週間もち', price: '¥9,000' },
      { name: 'ボリュームラッシュ', description: '2〜4本束付け・ふんわり仕上がり', price: '¥12,000' },
      { name: 'まつげパーマ', description: 'カール・ナチュラルカール選択可', price: '¥8,000' },
      { name: 'リムーブのみ', description: '他サロン施術も対応可能', price: '¥2,000' },
    ],
    note: 'まつげパーマとエクステの同日施術は不可',
  },
];

function MenuCard({ category, delay }: { category: MenuCategory; delay: number }) {
  const { ref, isInView } = useInView<HTMLDivElement>({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      className="bg-white border border-salon-light overflow-hidden transition-all duration-700"
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(40px)',
        transitionDelay: `${delay}ms`,
        borderRadius: '4px',
      }}
    >
      {/* Image */}
      <div className="relative overflow-hidden" style={{ height: '200px' }}>
        <img
          src={category.image}
          alt={category.label}
          className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/30 to-transparent" />
        <div className="absolute top-4 left-4">
          <span
            className="bg-offwhite/90 text-gold px-3 py-1 font-sans-jp"
            style={{ fontSize: '10px', letterSpacing: '0.2em' }}
          >
            {category.enLabel}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 flex items-center justify-center text-gold">
            <i className={`${category.icon} text-xl`} />
          </div>
          <h3 className="font-serif-jp text-charcoal font-medium" style={{ fontSize: 'clamp(16px, 2.5vw, 20px)' }}>
            {category.label}
          </h3>
        </div>

        <div className="gold-divider mb-5" />

        {/* Price list */}
        <div className="space-y-4">
          {category.items.map((item) => (
            <div key={item.name} className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="font-sans-jp text-charcoal font-medium" style={{ fontSize: 'clamp(13px, 1.8vw, 15px)' }}>
                  {item.name}
                </p>
                <p className="text-salon-gray font-sans-jp mt-0.5" style={{ fontSize: '12px' }}>
                  {item.description}
                  {item.duration && (
                    <span className="ml-2 text-gold">/ {item.duration}</span>
                  )}
                </p>
              </div>
              <span
                className="flex-shrink-0 font-sans-jp text-charcoal font-semibold"
                style={{ fontSize: 'clamp(13px, 1.8vw, 15px)' }}
              >
                {item.price}
              </span>
            </div>
          ))}
        </div>

        {category.note && (
          <p
            className="mt-5 pt-4 border-t border-salon-light text-salon-gray font-sans-jp"
            style={{ fontSize: '12px', letterSpacing: '0.05em' }}
          >
            ※ {category.note}
          </p>
        )}
      </div>
    </div>
  );
}

export default function Menu() {
  const { ref: titleRef, isInView: titleInView } = useInView<HTMLDivElement>();

  return (
    <section id="menu" className="py-20 sm:py-28 lg:py-36 bg-offwhite">
      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        {/* Section title */}
        <div
          ref={titleRef}
          className="mb-12 sm:mb-16 transition-all duration-700"
          style={{
            opacity: titleInView ? 1 : 0,
            transform: titleInView ? 'translateY(0)' : 'translateY(32px)',
          }}
        >
          <p className="section-label mb-4">MENU &amp; PRICE</p>
          <h2 className="font-serif-jp text-charcoal leading-snug mb-4" style={{ fontSize: 'clamp(28px, 5vw, 48px)' }}>
            施術メニューと
            <br />
            明朗会計
          </h2>
          <p className="font-sans-jp text-salon-gray" style={{ fontSize: 'clamp(12px, 1.8vw, 14px)' }}>
            すべて税込価格です。追加料金は一切ございません。
          </p>
        </div>

        {/* Menu cards - vertical stack */}
        <div className="space-y-8 sm:space-y-10">
          {menuCategories.map((category, idx) => (
            <MenuCard key={category.id} category={category} delay={idx * 120} />
          ))}
        </div>

        {/* Bottom note */}
        <div
          className="mt-10 sm:mt-14 pt-8 border-t border-salon-light"
        >
          <p className="font-sans-jp text-salon-gray text-center" style={{ fontSize: '13px', letterSpacing: '0.05em' }}>
            ご予約・施術内容のご相談は、お電話またはLINEにてお気軽にお問い合わせください。
          </p>
        </div>
      </div>
    </section>
  );
}
